import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { api, ApiError } from "./client.js";
import type {
  AdminStream,
  AdminUser,
  CreateGrantBody,
  CreateNodeBody,
  CreateNodeResult,
  Grant,
  MediaNode,
  MeDTO,
  Paginated,
  Role,
  SetupFinishBody,
  SetupStatus,
  SiteSettings,
  UpdateNodeBody,
  UpdateSettingsBody,
} from "./types.js";

export const qk = {
  me: ["me"] as const,
  setupStatus: ["setup", "status"] as const,
  users: (query: string) => ["users", query] as const,
  user: (id: string) => ["user", id] as const,
  grants: (userId: string) => ["grants", userId] as const,
  nodes: ["nodes"] as const,
  node: (id: string) => ["node", id] as const,
  streams: (status: string) => ["streams", status] as const,
  settings: ["settings"] as const,
};

// ─────────────────────────── Me / setup ───────────────────────────

export function useMe(
  options?: Partial<UseQueryOptions<MeDTO | null, ApiError>>,
) {
  return useQuery<MeDTO | null, ApiError>({
    queryKey: qk.me,
    queryFn: async () => {
      try {
        return await api.get<MeDTO>("/api/me");
      } catch (e) {
        // 401 = not logged in; surface as null rather than an error.
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    retry: false,
    staleTime: 30_000,
    ...options,
  });
}

export function useSetupStatus() {
  return useQuery<SetupStatus, ApiError>({
    queryKey: qk.setupStatus,
    queryFn: () => api.get<SetupStatus>("/setup/status"),
    retry: false,
    staleTime: 10_000,
  });
}

// ─────────────────────────── Users ───────────────────────────

export function useUsers(query: string) {
  return useQuery<Paginated<AdminUser>, ApiError>({
    queryKey: qk.users(query),
    queryFn: () =>
      api.get<Paginated<AdminUser>>(
        `/api/admin/users?query=${encodeURIComponent(query)}`,
      ),
    placeholderData: (prev) => prev,
  });
}

export function useUser(id: string | null) {
  return useQuery<AdminUser, ApiError>({
    queryKey: qk.user(id ?? ""),
    queryFn: () => api.get<AdminUser>(`/api/admin/users/${id}`),
    enabled: !!id,
  });
}

export function useSetRole(userId: string) {
  const qc = useQueryClient();
  return useMutation<AdminUser, ApiError, Role>({
    mutationFn: (role) =>
      api.patch<AdminUser>(`/api/admin/users/${userId}/role`, { role }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.user(userId) });
      qc.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

// ─────────────────────────── Grants ───────────────────────────

export function useGrants(userId: string | null) {
  return useQuery<Grant[], ApiError>({
    queryKey: qk.grants(userId ?? ""),
    queryFn: () => api.get<Grant[]>(`/api/admin/users/${userId}/grants`),
    enabled: !!userId,
  });
}

export function useAddGrant(userId: string) {
  const qc = useQueryClient();
  return useMutation<Grant, ApiError, CreateGrantBody>({
    mutationFn: (body) =>
      api.post<Grant>(`/api/admin/users/${userId}/grants`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.grants(userId) });
      qc.invalidateQueries({ queryKey: qk.user(userId) });
      qc.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

export function useDeleteGrant(userId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (grantId) => api.del<void>(`/api/admin/grants/${grantId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.grants(userId) });
      qc.invalidateQueries({ queryKey: qk.user(userId) });
      qc.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

// ─────────────────────────── Nodes ───────────────────────────

export function useNodes() {
  return useQuery<MediaNode[], ApiError>({
    queryKey: qk.nodes,
    queryFn: () => api.get<MediaNode[]>("/api/admin/nodes"),
    refetchInterval: 15_000,
  });
}

export function useNode(id: string | null) {
  return useQuery<MediaNode, ApiError>({
    queryKey: qk.node(id ?? ""),
    queryFn: () => api.get<MediaNode>(`/api/admin/nodes/${id}`),
    enabled: !!id,
    refetchInterval: 15_000,
  });
}

export function useCreateNode() {
  const qc = useQueryClient();
  return useMutation<CreateNodeResult, ApiError, CreateNodeBody>({
    mutationFn: (body) =>
      api.post<CreateNodeResult>("/api/admin/nodes", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.nodes }),
  });
}

export function usePatchNode(id: string) {
  const qc = useQueryClient();
  return useMutation<MediaNode, ApiError, UpdateNodeBody>({
    mutationFn: (patch) =>
      api.patch<MediaNode>(`/api/admin/nodes/${id}`, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.nodes });
      qc.invalidateQueries({ queryKey: qk.node(id) });
    },
  });
}

export function useDeleteNode() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => api.del<void>(`/api/admin/nodes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.nodes }),
  });
}

// ─────────────────────────── Streams ───────────────────────────

export function useStreams(status: "live" | "recent") {
  return useQuery<AdminStream[], ApiError>({
    queryKey: qk.streams(status),
    queryFn: () => api.get<AdminStream[]>(`/api/admin/streams?status=${status}`),
    refetchInterval: status === "live" ? 10_000 : false,
  });
}

export function useSetFeatured() {
  const qc = useQueryClient();
  return useMutation<
    AdminStream,
    ApiError,
    { id: string; featured: boolean }
  >({
    mutationFn: ({ id, featured }) =>
      api.patch<AdminStream>(`/api/admin/streams/${id}/featured`, { featured }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["streams"] }),
  });
}

export function useForceEnd() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => api.post<void>(`/api/admin/streams/${id}/force-end`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["streams"] }),
  });
}

// ─────────────────────────── Settings ───────────────────────────

export function useSettings() {
  return useQuery<SiteSettings, ApiError>({
    queryKey: qk.settings,
    queryFn: () => api.get<SiteSettings>("/api/admin/settings"),
  });
}

export function usePatchSettings() {
  const qc = useQueryClient();
  return useMutation<SiteSettings, ApiError, UpdateSettingsBody>({
    mutationFn: (body) =>
      api.patch<SiteSettings>("/api/admin/settings", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.settings }),
  });
}

// ─────────────────────────── Setup mutations ───────────────────────────

export function useSetupAdmin() {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean }, ApiError, void>({
    mutationFn: () => api.post<{ ok: boolean }>("/setup/admin", {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.me });
      qc.invalidateQueries({ queryKey: qk.setupStatus });
    },
  });
}

export function useSetupFinish() {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean }, ApiError, SetupFinishBody>({
    mutationFn: (body) => api.post<{ ok: boolean }>("/setup/finish", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.setupStatus });
      qc.invalidateQueries({ queryKey: qk.me });
    },
  });
}
